import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { ClipboardPaste, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";

import { MedicoSchemas, type PasteInput } from "@/api/router/medico/schemas";
import { useMedicoPaste } from "@/hooks/use-medico";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UnsavedGuard } from "@/components/shared/form/unsaved-guard";
import { MedicoLabels } from "@/lib/labels/medico";

import { Modal } from "@/components/ui/modal";

export function AvulsaModal({ onClose }: { onClose: () => void }) {
	const paste = useMedicoPaste();
	const [confirmingClose, setConfirmingClose] = useState(false);
	const {
		register,
		handleSubmit,
		formState: { isDirty, isValid },
	} = useForm<PasteInput>({
		resolver: arktypeResolver(MedicoSchemas.paste),
		mode: "onChange",
		defaultValues: { title: "", text: "" },
	});

	const submit = handleSubmit((values) => {
		paste.mutate(values, { onSuccess: onClose });
	});

	const requestClose = () => {
		if (isDirty && !paste.isPending) {
			setConfirmingClose(true);

			return;
		}

		onClose();
	};

	return (
		<Modal
			title={MedicoLabels.paste.title}
			description={MedicoLabels.paste.subtitle}
			onClose={requestClose}
			isPending={paste.isPending}
		>
			{confirmingClose && (
				<UnsavedGuard onStay={() => setConfirmingClose(false)} onDiscard={onClose} />
			)}

			<form onSubmit={submit} className="flex flex-col gap-3">
				<Input
					type="text"
					{...register("title")}
					autoFocus
					disabled={paste.isPending}
					placeholder={MedicoLabels.paste.titlePlaceholder}
				/>

				<Textarea
					{...register("text")}
					disabled={paste.isPending}
					onKeyDown={(event) => {
						if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
							event.preventDefault();
							void submit();
						}
					}}
					placeholder={MedicoLabels.paste.textPlaceholder}
					rows={12}
					className="font-mono leading-relaxed"
				/>

				{paste.isError && <p className="text-xs text-destructive">{MedicoLabels.paste.error}</p>}

				<div className="mt-1 flex items-center justify-end gap-2">
					{!isValid && (
						<span className="mr-auto text-xs text-muted-foreground">
							{MedicoLabels.paste.required}
						</span>
					)}

					<Button
						type="button"
						size="sm"
						variant="secondary"
						disabled={paste.isPending}
						onClick={requestClose}
					>
						Cancelar
					</Button>

					<Button
						size="sm"
						type="submit"
						variant="default"
						className="px-4"
						disabled={!isValid || paste.isPending}
					>
						{paste.isPending && <Loader2 className="animate-spin" />}
						{!paste.isPending && <ClipboardPaste />}
						{paste.isPending && "registrando transcrição..."}
						{!paste.isPending && MedicoLabels.paste.submit}
					</Button>
				</div>
			</form>
		</Modal>
	);
}
